import { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, RefreshCw, Download, Upload as UploadIcon, Search, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import io from 'socket.io-client';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// const WA_SERVICE_URL = 'http://localhost:3001';
const WA_SERVICE_URL = process.env.REACT_APP_WA_SERVICE_URL;

const MessageHistory = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    fetchMessages();

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    // Connect to WebSocket for real-time updates
    const newSocket = io(WA_SERVICE_URL);
    setSocket(newSocket);

    // Join user-specific room
    newSocket.emit('join', { userId: user.id });

    newSocket.on('message', (newMessage) => {
      setMessages(prev => [newMessage, ...prev]);
      toast.success(`Pesan baru ${newMessage.type === 'received' ? 'masuk' : 'terkirim'}!`);
    });

    return () => {
      if (newSocket) newSocket.disconnect();
    };
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Gagal mengambil riwayat pesan');
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus semua riwayat pesan?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages([]);
      toast.success('Riwayat pesan berhasil dihapus');
    } catch (error) {
      console.error('Error clearing messages:', error);
      toast.error('Gagal menghapus riwayat pesan');
    }
  };

  const filteredMessages = messages.filter(msg => {
    const searchLower = searchTerm.toLowerCase();
    return (
      msg.content?.toLowerCase().includes(searchLower) ||
      msg.phone?.toLowerCase().includes(searchLower) ||
      msg.fromNumber?.toLowerCase().includes(searchLower) ||
      msg.fromName?.toLowerCase().includes(searchLower)
    );
  });

  const getMessageIcon = (type, mediaType) => {
    if (type === 'sent') {
      return <UploadIcon className="text-green-600" size={16} />;
    }
    return <Download className="text-blue-600" size={16} />;
  };

  const formatTimestamp = (timestamp) => {
    try {
      return format(new Date(timestamp), 'dd MMM yyyy HH:mm');
    } catch (error) {
      return timestamp;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800">Riwayat Pesan</h1>
          <p className="text-gray-600 mt-2">{filteredMessages.length} pesan ditemukan</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="refresh-history-button"
            onClick={fetchMessages}
            className="bg-green-600 hover:bg-green-700"
          >
            <RefreshCw size={18} className="mr-2" />
            Refresh
          </Button>
          <Button
            data-testid="clear-history-button"
            onClick={handleClearHistory}
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            <Trash2 size={18} className="mr-2" />
            Hapus Semua
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="glass-effect">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              data-testid="search-input"
              type="text"
              placeholder="Cari pesan, nomor, atau nama..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-gray-300 focus:border-green-500"
            />
          </div>
        </CardContent>
      </Card>

      {/* Messages List */}
      <Card className="glass-effect">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="text-green-600" size={24} />
            Semua Pesan
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Tidak ada pesan ditemukan</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto scrollbar-hide">
              {filteredMessages.map((msg, index) => (
                <div
                  key={msg.id || index}
                  data-testid={`message-item-${index}`}
                  className="p-4 rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50/50 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getMessageIcon(msg.type, msg.mediaType)}
                        <Badge
                          variant={msg.type === 'sent' ? 'default' : 'secondary'}
                          className={msg.type === 'sent' ? 'bg-green-600' : 'bg-blue-600'}
                        >
                          {msg.type === 'sent' ? 'Terkirim' : 'Masuk'}
                        </Badge>
                        {msg.mediaType && msg.mediaType !== 'text' && (
                          <Badge variant="outline" className="border-purple-300 text-purple-600">
                            {msg.mediaType}
                          </Badge>
                        )}
                      </div>
                      <p className="font-semibold text-gray-800 mb-1">
                        {msg.type === 'sent'
                          ? `Ke: ${msg.phone}`
                          : `Dari: ${msg.fromName || msg.fromNumber}`}
                      </p>
                      <p className="text-gray-700 break-words">{msg.content}</p>
                      {msg.mediaUrl && (
                        <a
                          href={`${WA_SERVICE_URL}${msg.mediaUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:underline text-sm mt-1 inline-block"
                        >
                          Lihat Media
                        </a>
                      )}
                    </div>
                    <div className="text-right text-sm text-gray-500 ml-4">
                      {formatTimestamp(msg.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MessageHistory;
